// Bundle product selection component - Custom implementation
if (!customElements.get('gallery-with-products')) {
  class GalleryWithProducts extends HTMLElement {
    constructor() {
      super();
      this.modal = null;
      this.bundleData = {
        products: new Map(),
        minItemsRequired: 2,
        variantQuantities: new Map() // Track quantities of each variant in current bundle
      };
      this.currentSelector = null;
      this.currentProduct = null;
      this.selectedVariant = null;
    }

    connectedCallback() {
      this.setupEventListeners();
      this.initializeBundleState();
      
      // Debounced validation to prevent infinite loops
      this.validateBundlesDebounced = this.debounce(this.validateBundlesInCart.bind(this), 1000);
      this.isValidating = false;
      
      // Listen for cart changes to validate bundles
      document.addEventListener('theme:cart:change', () => {
        console.log('🔄 Cart change event detected, triggering bundle validation...');
        if (!this.isValidating) {
          // Trigger immediate validation for debugging
          console.log('🚀 Triggering immediate validation for debugging...');
          this.validateBundlesInCart();
          
          // Also trigger the debounced version as backup
          this.validateBundlesDebounced();
        } else {
          console.log('⏳ Bundle validation already in progress, skipping...');
        }
      });
    }

    initializeBundleState() {
      // Get all free items containers instead of just one
      const freeItemsContainers = this.querySelectorAll('.free-items-container');
      
      // Initialize bundle data for multiple containers
      this.bundleData.freeItemsData = [];
      
      if (freeItemsContainers.length > 0) {
        freeItemsContainers.forEach((container, index) => {
          const containerData = {
            element: container,
            minItemsRequired: parseInt(container.dataset.minimumItemsRequired) || 2,
            giftsSettings: container.dataset.giftsSettings || 'display_only',
            oneForEachProduct: container.dataset.oneForEachProduct === 'true',
            oneForEachProductType: container.dataset.oneForEachProductType || 'big-folk',
            chestTextClosed: container.dataset.chestTextClosed,
            chestTextOpen: container.dataset.chestTextOpen,
            freeProducts: this.parseFreeProductsData(container)
          };
          this.bundleData.freeItemsData.push(containerData);
        });
        
        // Use the lowest minimum items required across all containers
        this.bundleData.minItemsRequired = Math.min(...this.bundleData.freeItemsData.map(data => data.minItemsRequired));
      }
      
      this.updateBuyButtonState();
      this.updateChestState();
    }

    setupEventListeners() {
      this.addEventListener('click', (event) => {
        // Check for remove button FIRST to prevent modal opening
        const removeButton = event.target.closest('.bundle-item-remove');
        if (removeButton) {
          // console.log('Remove button clicked');
          event.stopPropagation(); // Prevent event from bubbling up to selector
          const selector = removeButton.closest('.products-content__item-wrapper-selector');
          this.removeProductFromBundle(selector);
          return;
        }

        const buyButton = event.target.closest('.buy-button');
        if (buyButton) {
          event.preventDefault(); // Prevent default form submission/page reload
          this.handleBundlePurchase();
          return;
        }

        const selector = event.target.closest('.products-content__item-wrapper-selector');
        if (selector) {
          // Check if selector is disabled
          if (selector.classList.contains('selector-disabled') || selector.style.display === 'none') {
            console.log('🚫 Cannot select disabled selector');
            return;
          }
          
          this.currentSelector = selector;
          const productHandle = selector.dataset.productHandle;
          this.openProductSelectionModal(productHandle);
          return;
        }
      });
    }

    async openProductSelectionModal(productHandle) {
      if (!this.modal) {
        this.createModal();
      }

      this.clearModalError(); // Clear any existing modal errors
      this.modal.classList.add('loading');
      this.modal.showModal();

      try {
        // Fetch product data via Shopify API
        const response = await fetch(`/products/${productHandle}.js`);
        if (!response.ok) {
          throw new Error('Failed to fetch product data');
        }

        this.currentProduct = await response.json();
        this.selectedVariant = this.currentProduct.variants[0]; // Default to first variant
        
        // Build custom modal content
        this.buildProductModal();
        
      } catch (error) {
        console.error('Error loading product:', error);
        this.modal.querySelector('.product-popup__content').innerHTML = '<p>Error loading product details</p>';
      } finally {
        this.modal.classList.remove('loading');
      }
    }

    buildProductModal() {
      const modalContent = `
        <div class="product-modal__images">
          ${this.buildProductImages()}
        </div>
        
        <div class="product-modal__details">
        <div class="product-modal__details-inner">
          <h2 class="product-modal__title">${this.currentProduct.title}</h2>
          <div class="product-modal__price">
            ${this.formatPrice(this.selectedVariant.price)}
            ${this.selectedVariant.compare_at_price > this.selectedVariant.price ? 
              `<span class="price-compare">${this.formatPrice(this.selectedVariant.compare_at_price)}</span>` : ''}
          </div>
          
          <div class="product-modal__availability" id="availability-status">
            <div class="availability-loading">Checking availability...</div>
          </div>
          
          ${this.currentProduct.description ? `<div class="product-modal__description">${this.currentProduct.description}</div>` : ''}
          </div>
          <form class="product-modal__form">
            ${this.buildVariantSelectors()}
            
            <button type="button" class="btn btn--primary btn--solid product-select-button" disabled>
              Checking availability...
            </button>
          </form>
        </div>
      `;

      const contentArea = this.modal.querySelector('.product-popup__content');
      contentArea.innerHTML = modalContent;
      
      // Add event listeners
      this.setupModalEventListeners();
      
      // Check availability for initial variant
      this.updateVariantAvailability();
    }

    buildProductImages() {
      const images = this.currentProduct.images || [];
      if (images.length === 0) {
        return '<div class="product-modal__placeholder">No image available</div>';
      }

      // Transform string URLs to objects for consistency
      const imageObjects = images.map((imageUrl, index) => {
        const fullUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
        return {
          src: fullUrl,
          id: `img-${index}`, // Generate consistent IDs
          alt: this.currentProduct.title
        };
      });

      // Find featured image using same fallback logic
      let featuredImage = null;
      
      // Try variant featured image first
      if (this.selectedVariant.featured_image && this.selectedVariant.featured_image.src) {
        featuredImage = this.selectedVariant.featured_image;
      }
      // Try variant image
      else if (this.selectedVariant.image && this.selectedVariant.image.src) {
        featuredImage = this.selectedVariant.image;
      }
      // Try product featured image
      else if (this.currentProduct.featured_image && this.currentProduct.featured_image.src) {
        featuredImage = this.currentProduct.featured_image;
      }
      // Fall back to first image
      else {
        featuredImage = imageObjects[0];
      }

      if (!featuredImage || !featuredImage.src) {
        return '<div class="product-modal__placeholder">No image available</div>';
      }
      
      return `
        <div class="product-modal__image-main">
          <img src="${this.getImageUrl(featuredImage.src, '500x500')}" 
               alt="${this.currentProduct.title}"
               class="product-modal__main-image">
        </div>
        ${imageObjects.length > 1 ? `
          <div class="product-modal__image-thumbs">
            ${imageObjects.map(image => `
              <button type="button" class="product-modal__thumb ${image.id === featuredImage.id ? 'active' : ''}" 
                      data-image-src="${image.src}"
                      data-image-id="${image.id}">
                <img src="${this.getImageUrl(image.src, '100x100')}" alt="${this.currentProduct.title}">
              </button>
            `).join('')}
          </div>
        ` : ''}
      `;
    }

    buildVariantSelectors() {
      if (this.currentProduct.variants.length <= 1) {
        return '';
      }

      const options = this.currentProduct.options || [];
      // console.log('Options:', options);
      return options.map((option, optionIndex) => {
        const optionValues = [...new Set(this.currentProduct.variants.map(v => v.options[optionIndex]))];

        return `
          <div class="product-modal__option">
            <label class="product-modal__option-label">${option.name}</label>
            <select class="product-modal__select" data-option-index="${optionIndex}">
              ${optionValues.map(value => `
                <option value="${value}" ${this.selectedVariant.options[optionIndex] === value ? 'selected' : ''}>
                  ${value}
                </option>
              `).join('')}
            </select>
          </div>
        `;
      }).join('');
    }

    buildPersonalizationField() {
      return `
        <div class="product-modal__option">
          <label class="product-modal__option-label">
            Letter Personalisation Text <span class="required">*</span>
          </label>
          <small class="product-modal__hint">
            Name for personalised letter from Father Christmas
          </small>
          <input type="text" 
                 class="product-modal__input personalization-input" 
                 placeholder="Enter personalisation text"
                 maxlength="20"
                 required>
        </div>
      `;
    }

    setupModalEventListeners() {
      const modal = this.modal.querySelector('.product-popup__content');
      
      // Variant selection
      const selects = modal.querySelectorAll('.product-modal__select');
      selects.forEach(select => {
        select.addEventListener('change', () => {
          this.updateSelectedVariant();
        });
      });

      // Image thumbnails - updated to use data-image-src
      const thumbs = modal.querySelectorAll('.product-modal__thumb');
      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const imageSrc = thumb.dataset.imageSrc;
          const imageId = thumb.dataset.imageId;
          this.updateMainImage(imageSrc, imageId);
        });
      });

      // Product selection
      const selectButton = modal.querySelector('.product-select-button');
      selectButton.addEventListener('click', () => {
        this.handleProductSelection();
      });
    }

    updateSelectedVariant() {
      const modal = this.modal.querySelector('.product-popup__content');
      const selects = modal.querySelectorAll('.product-modal__select');
      
      const selectedOptions = Array.from(selects).map(select => select.value);
      
      // Find matching variant
      this.selectedVariant = this.currentProduct.variants.find(variant => {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      }) || this.currentProduct.variants[0];

      // console.log('Updated selected variant:', this.selectedVariant); // Debug log

      // Update price
      const priceElement = modal.querySelector('.product-modal__price');
      priceElement.innerHTML = `
        ${this.formatPrice(this.selectedVariant.price)}
        ${this.selectedVariant.compare_at_price > this.selectedVariant.price ? 
          `<span class="price-compare">${this.formatPrice(this.selectedVariant.compare_at_price)}</span>` : ''}
      `;

      // Update main image if variant has featured image
      let variantImageSrc = null;
      if (this.selectedVariant.featured_image && this.selectedVariant.featured_image.src) {
        variantImageSrc = this.selectedVariant.featured_image.src;
      } else if (this.selectedVariant.image && this.selectedVariant.image.src) {
        variantImageSrc = this.selectedVariant.image.src;
      }

      if (variantImageSrc) {
        this.updateMainImage(variantImageSrc);
      }

      // Check availability for new variant with a small delay to ensure DOM is ready
      setTimeout(() => {
        this.updateVariantAvailability();
      }, 50);
    }

    updateMainImage(imageSrc, imageId = null) {
      const modal = this.modal.querySelector('.product-popup__content');
      const mainImage = modal.querySelector('.product-modal__main-image');
      
      if (imageSrc) {
        mainImage.src = this.getImageUrl(imageSrc, '500x500');
        
        // Update active thumb if imageId provided
        if (imageId) {
          modal.querySelectorAll('.product-modal__thumb').forEach(thumb => {
            thumb.classList.toggle('active', thumb.dataset.imageId == imageId);
          });
        }
      }
    }

    handleProductSelection() {
      // Add to bundle
      this.addProductToBundle({
        productId: this.currentProduct.id,
        variantId: this.selectedVariant.id,
        title: this.currentProduct.title,
        variantTitle: this.selectedVariant.title,
        price: this.selectedVariant.price,
        handle: this.currentProduct.handle,
        image: this.selectedVariant.featured_image || this.currentProduct.featured_image
      });

      // Close modal
      this.modal.close();
    }

    isBigFolkProduct(productHandle) {
      const bigFolkSelectors = this.querySelectorAll('.products-content__item-wrapper-selector[data-product-handle="' + productHandle + '"]');
      const isBigFolk = bigFolkSelectors.length > 0 && bigFolkSelectors[0].closest('.products-content__item').querySelector('.icon-container svg') !== null;
      
      const isBigFolkProduct = Array.from(this.querySelectorAll('.products-content__item-title')).some(title => 
        title.textContent.includes('Big Folk') && 
        title.closest('.products-content__item').querySelector(`[data-product-handle="${productHandle}"]`)
      );
      
      return isBigFolk || isBigFolkProduct;
    }

    addProductToBundle(productData) {
      if (!this.currentSelector) return;

      // Add product type information to productData
      const productType = this.getProductTypeFromSelector(this.currentSelector);
      productData.productType = productType;

      this.bundleData.products.set(this.currentSelector, productData);
      
      // Track variant quantities
      const variantId = productData.variantId;
      const currentCount = this.bundleData.variantQuantities.get(variantId) || 0;
      this.bundleData.variantQuantities.set(variantId, currentCount + 1);
      
      this.updateSelectorState(this.currentSelector, productData);
      this.updateBuyButtonState();
      this.updateChestState();
      this.updateBundleSummary();
      
      // Enable next selector for this product type
      this.enableNextSelector(productType);

      // console.log('Bundle updated:', {
      //   products: this.bundleData.products,
      //   variantQuantities: this.bundleData.variantQuantities
      // });
    }

    updateSelectorState(selector, productData) {
      selector.classList.add('selected');
      
      // Get the best available image - handle both string URLs and image objects
      let imageSrc = '';
      // console.log('Product data:', productData);
      
      if (productData.image) {
        // If image is a string URL
        if (typeof productData.image === 'string') {
          imageSrc = this.getImageUrl(productData.image, '150x150');
        }
        // If image is an object with src property
        else if (productData.image.src) {
          imageSrc = this.getImageUrl(productData.image.src, '150x150');
        }
      }
      
      selector.innerHTML = `
        <div class="bundle-item-selected">
          ${imageSrc ? `
            <img src="${imageSrc}" 
                 alt="${productData.title}" 
                 class="bundle-item-image">
          ` : `
            <div class="bundle-item-placeholder">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
                <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" stroke-width="2"/>
                <path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
          `}
          <button type="button" class="bundle-item-remove" aria-label="Remove ${productData.title}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      `;
    }

    removeProductFromBundle(selector) {
      const productData = this.bundleData.products.get(selector);
      
      if (productData) {
        // Update variant quantities
        const variantId = productData.variantId;
        const currentCount = this.bundleData.variantQuantities.get(variantId) || 0;
        if (currentCount <= 1) {
          this.bundleData.variantQuantities.delete(variantId);
        } else {
          this.bundleData.variantQuantities.set(variantId, currentCount - 1);
        }
        
        // Disable the last enabled selector for this product type if needed
        this.disableLastSelector(productData.productType);
      }
      
      this.bundleData.products.delete(selector);
      selector.classList.remove('selected');
      selector.innerHTML = '';
      this.updateBuyButtonState();
      this.updateChestState();
      this.updateBundleSummary();
    }

    updateBuyButtonState() {
      const buyButton = this.querySelector('.buy-button');
      const selectedCount = this.bundleData.products.size;
      
      if (buyButton) {
        // Clear any existing errors when state changes
        this.clearError();
        
        if (selectedCount >= this.bundleData.minItemsRequired) {
          buyButton.disabled = false;
          buyButton.classList.remove('disabled');
          
          // Check if there are free products to include
          const freeProducts = this.getFreeProducts();
          if (freeProducts.length > 0) {
            buyButton.textContent = `Proceed to Checkout`;
          }
        } else {
          buyButton.disabled = true;
          buyButton.classList.add('disabled');
          buyButton.textContent = `Select ${this.bundleData.minItemsRequired - selectedCount} more item${this.bundleData.minItemsRequired - selectedCount !== 1 ? 's' : ''}`;
        }
      }
    }

    showError(message) {
      this.clearError(); // Clear any existing error first
      
      const buyButtonContainer = this.querySelector('.buy-button-container');
      if (!buyButtonContainer) return;

      const errorDiv = document.createElement('div');
      errorDiv.className = 'bundle-error-message';
      errorDiv.textContent = message;
      
      buyButtonContainer.appendChild(errorDiv);
      
      // Auto-hide error after 5 seconds
      setTimeout(() => this.clearError(), 5000);
    }

    clearError() {
      const existingError = this.querySelector('.bundle-error-message');
      if (existingError) {
        existingError.remove();
      }
    }

    showModalError(message) {
      this.clearModalError(); // Clear any existing modal error first
      
      const modalForm = this.modal?.querySelector('.product-modal__form');
      if (!modalForm) return;

      const errorDiv = document.createElement('div');
      errorDiv.className = 'modal-error-message';
      errorDiv.textContent = message;
      
      // Insert at the beginning of the form
      modalForm.insertBefore(errorDiv, modalForm.firstChild);
      
      // Auto-hide error after 5 seconds
      setTimeout(() => this.clearModalError(), 5000);
    }

    clearModalError() {
      const existingError = this.modal?.querySelector('.modal-error-message');
      if (existingError) {
        existingError.remove();
      }
    }

    updateChestState() {
      const selectedCount = this.bundleData.products.size;
      
      // Update each free items container
      if (this.bundleData.freeItemsData && this.bundleData.freeItemsData.length > 0) {
        this.bundleData.freeItemsData.forEach(containerData => {
          const { element, minItemsRequired, chestTextOpen, chestTextClosed } = containerData;
          const titleElement = element.querySelector('.free-items__title');
          
          if (selectedCount >= minItemsRequired) {
            element.classList.add('chest-open');
            if (titleElement && chestTextOpen) {
              const iconContainer = titleElement.querySelector('.icon-container');
              titleElement.innerHTML = '';
              if (iconContainer) {
                titleElement.appendChild(iconContainer);
              }
              titleElement.appendChild(document.createTextNode(chestTextOpen));
            }
          } else {
            element.classList.remove('chest-open');
            if (titleElement && chestTextClosed) {
              const iconContainer = titleElement.querySelector('.icon-container');
              titleElement.innerHTML = '';
              if (iconContainer) {
                titleElement.appendChild(iconContainer);
              }
              titleElement.appendChild(document.createTextNode(chestTextClosed));
            }
          }
        });
      }
    }

    async handleBundlePurchase() {
      if (this.bundleData.products.size < this.bundleData.minItemsRequired) {
        this.showError(`Please select at least ${this.bundleData.minItemsRequired} items before purchasing`);
        return;
      }

      const buyButton = this.querySelector('.buy-button');
      buyButton.disabled = true;
      buyButton.classList.add('is-loading');
      buyButton.textContent = 'Validating items...';

      try {
        // Generate unique bundle ID with minimum items encoded for validation
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substr(2, 9);
        const bundleId = `bundle_min${this.bundleData.minItemsRequired}_${timestamp}_${randomString}`;
        
        // console.log('🎯 BUNDLE TRACKER v3.0 (Anti-Exploit) - Generated bundle ID:', bundleId);
        // console.log('📊 Bundle requires minimum items:', this.bundleData.minItemsRequired);

        // Get selected products
        const selectedItems = Array.from(this.bundleData.products.values()).map(product => {
          const variantId = parseInt(product.variantId);
          if (isNaN(variantId)) {
            console.error('Invalid variant ID:', product.variantId, 'for product:', product.title);
            throw new Error(`Invalid variant ID for ${product.title}`);
          }
          
          // Create properties object with encoded bundle ID
          const properties = {
            '_bundle_id': bundleId,
            '_bundle_item': 'true',
            '_bundle_min_required': this.bundleData.minItemsRequired.toString() // Additional redundancy
          };
          
          // console.log('✅ Setting bundle item properties (anti-exploit):', properties);
          
          return {
            id: variantId,
            quantity: 1,
            title: product.title,
            properties: properties
          };
        });

        // console.log('Selected items with IDs:', selectedItems.map(item => ({
        //   title: item.title,
        //   id: item.id,
        //   type: typeof item.id
        // }))); 

        // Validate selected items first
        buyButton.textContent = 'Checking selected items...';
        
        // Group items by variant to check accumulated quantities
        const variantGroups = new Map();
        selectedItems.forEach(item => {
          const existing = variantGroups.get(item.id) || { quantity: 0, items: [] };
          existing.quantity += item.quantity;
          existing.items.push(item);
          variantGroups.set(item.id, existing);
        });
        
        // Check availability for each variant group
        const selectedItemsValidation = [];
        for (const [variantId, group] of variantGroups) {
          // For this validation, temporarily clear the bundle quantities to get a clean check
          const originalBundleQuantity = this.bundleData.variantQuantities.get(variantId) || 0;
          this.bundleData.variantQuantities.set(variantId, 0);
          
          const availability = await this.checkCartAvailability(variantId, group.quantity);
          
          // Restore the bundle quantity
          this.bundleData.variantQuantities.set(variantId, originalBundleQuantity);
          
          selectedItemsValidation.push({
            variantId,
            group,
            availability
          });
        }

        // Check for any unavailable selected items
        const unavailableSelected = selectedItemsValidation.filter(validation => !validation.availability.available);
        if (unavailableSelected.length > 0) {
          const errorMessages = unavailableSelected.map(validation => 
            `"${validation.group.items[0].title}": ${validation.availability.reason}`
          );
          this.showError(`Cannot add to bundle:\n${errorMessages.join('\n')}`);
          return;
        }

        const freeItems = [];
        if (this.bundleData.products.size >= this.bundleData.minItemsRequired) {
          buyButton.textContent = 'Checking items...';
          
          // Process each container separately based on its settings
          if (this.bundleData.freeItemsData && this.bundleData.freeItemsData.length > 0) {
            for (const containerData of this.bundleData.freeItemsData) {
              const selectedCount = this.bundleData.products.size;
              
              // Only add to cart if this specific container has add_to_cart setting
              if (containerData.giftsSettings === 'add_to_cart' && selectedCount >= containerData.minItemsRequired) {
                for (const freeProduct of containerData.freeProducts) {
                  try {
                    const productResponse = await fetch(`/products/${freeProduct.handle}.js`);
                    if (productResponse.ok) {
                      const productData = await productResponse.json();
                      const firstAvailableVariant = productData.variants.find(variant => variant.available);
                      
                      if (firstAvailableVariant) {
                        const availability = await this.checkCartAvailability(firstAvailableVariant.id, 1);
                        
                        if (availability.available) {
                          const freeVariantId = parseInt(firstAvailableVariant.id);
                          if (isNaN(freeVariantId)) {
                            console.warn(`Invalid variant ID for free product ${freeProduct.title}:`, firstAvailableVariant.id);
                            continue;
                          }
                          
                          const freeItemProperties = {
                            '_free_gift': 'true',
                            '_bundle_gift': 'Bundle Gift',
                            '_bundle_id': bundleId,
                            '_bundle_free_item': 'true',
                            '_bundle_min_required': this.bundleData.minItemsRequired.toString()
                          };
                          
                          // console.log('✅ Setting free item properties (anti-exploit):', freeItemProperties);
                          
                          freeItems.push({
                            id: freeVariantId,
                            quantity: 1,
                            title: freeProduct.title,
                            properties: freeItemProperties
                          });
                        } else {
                          console.warn(`Free product "${freeProduct.title}" not available:`, availability.reason);
                        }
                      }
                    }
                  } catch (error) {
                    console.warn(`Could not fetch variant for free product ${freeProduct.handle}:`, error);
                  }
                }
              }
              // display_only containers are handled in updateBundleSummary()
            }
          }
        }

        // Combine selected and free items (only includes free items if they should be added to cart)
        const items = [...selectedItems, ...freeItems];

        buyButton.textContent = 'Adding to cart...';
        
        // Create a clean payload - ensure no reference issues
        const cartItems = [];
        for (const item of items) {
          const cartItem = {
            id: item.id,
            quantity: item.quantity
          };
          
          // Only add properties if they exist and aren't empty
          if (item.properties && Object.keys(item.properties).length > 0) {
            cartItem.properties = { ...item.properties };
          }
          
          cartItems.push(cartItem);
        }
        
        const cartPayload = { items: cartItems };

        // Final validation before sending
        console.log('Final cart payload validation:', {
          totalItems: cartItems.length,
          payload: cartPayload,
          stringified: JSON.stringify(cartPayload),
          stringifiedLength: JSON.stringify(cartPayload).length
        });

        // Debug: Log all properties being sent
        console.log('Properties being sent to cart:');
        cartItems.forEach((item, index) => {
          console.log(`Item ${index + 1} (${item.id}):`, {
            properties: item.properties,
            hasProperties: !!item.properties,
            propertyCount: item.properties ? Object.keys(item.properties).length : 0
          });
        });

        // Ensure all IDs are valid numbers
        for (const item of cartItems) {
          if (!Number.isInteger(item.id) || item.id <= 0) {
            throw new Error(`Invalid variant ID: ${item.id}`);
          }
          if (!Number.isInteger(item.quantity) || item.quantity <= 0) {
            throw new Error(`Invalid quantity: ${item.quantity}`);
          }
        }

        console.log('Adding anti-exploit bundle to cart:', {
          bundleId: bundleId,
          minItemsRequired: this.bundleData.minItemsRequired,
          selectedItems: selectedItems.length,
          freeItems: freeItems.length,
          bundleStructure: {
            paidItems: cartItems.filter(item => item.properties && item.properties._bundle_item === 'true').length,
            freeItems: cartItems.filter(item => item.properties && item.properties._bundle_free_item === 'true').length
          }
        });

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(cartPayload)
        });

        if (!response.ok) {
          const errorData = await response.text(); // Get raw response first
          console.error('Cart add failed:', {
            status: response.status,
            statusText: response.statusText,
            rawResponse: errorData,
            sentPayload: cartPayload
          });
          
          let parsedError;
          try {
            parsedError = JSON.parse(errorData);
          } catch (e) {
            parsedError = { message: errorData };
          }
          
          // Try to provide more specific error information
          if (response.status === 422) {
            // Check if the error response has specific details
            if (parsedError.message && parsedError.message.includes('maximum quantity')) {
              // Parse which specific item caused the issue
              const failedItems = items.filter(item => {
                return parsedError.message.includes(item.title) || 
                       parsedError.description?.includes(item.title) ||
                       parsedError.message.includes(item.id.toString());
              });
              
              if (failedItems.length > 0) {
                this.showError(`Cannot add these items (already at maximum in cart): ${failedItems.map(item => item.title).join(', ')}`);
              } else {
                this.showError('Some items are already at maximum quantity in your cart. Please check your cart and try again.');
              }
            } else if (parsedError.message && parsedError.message.includes('not available')) {
              this.showError('Some items are no longer available. Please refresh and try again.');
            } else {
              this.showError(`Cart Error (${response.status}): ${parsedError.message || 'Unknown validation error'}\n\nPlease check your cart and try again.`);
            }
          } else {
            this.showError('Error adding bundle to cart: ' + (parsedError.message || `HTTP ${response.status}: ${response.statusText}`));
          }
          return;
        }

        const result = await response.json();
        console.log('Bundle successfully added to cart:', result);

        // Debug: Check what properties actually made it to the cart
        try {
          const cartCheck = await this.getCurrentCart();
          console.log('Cart contents after bundle add:');
          cartCheck.items.forEach((item, index) => {
            if (item.properties && item.properties._bundle_id === bundleId) {
              console.log(`Bundle item ${index + 1}:`, {
                title: item.product_title,
                variant_title: item.variant_title,
                properties: item.properties,
                isBundleItem: item.properties._bundle_item === 'true',
                isFreeItem: item.properties._bundle_free_item === 'true'
              });
            }
          });
        } catch (error) {
          console.warn('Could not fetch cart for verification:', error);
        }

        this.resetBundle();

        // Update cart using the existing cart system
        await this.updateCartAndOpenDrawer();

      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        this.showError('Error adding bundle to cart: ' + error.message);
      } finally {
        buyButton.disabled = false;
        buyButton.classList.remove('is-loading');
        this.updateBuyButtonState();
      }
    }

    async updateCartAndOpenDrawer() {
      try {
        // Update cart using the existing cart-items element
        const cartItems = document.querySelector('cart-items');
        if (cartItems) {
          await cartItems.getCart();
        } else {
          // Fallback: manually fetch cart data and dispatch change event
          const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
          const freshCartData = await cartResponse.json();
          document.dispatchEvent(
            new CustomEvent('theme:cart:change', {
              bubbles: true,
              detail: {
                cartCount: freshCartData.item_count
              },
            })
          );
        }

        // Open cart drawer if using drawer cart type
        if (window.theme?.settings?.cartType === 'drawer') {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            cartDrawer.openCartDrawer();
          }
        }
        
        // Dispatch the product added event for any other listeners
        document.dispatchEvent(new CustomEvent('theme:product:added', { bubbles: true }));
        
      } catch (error) {
        console.error('Error updating cart:', error);
        // Fallback: refresh the page if cart update fails
        window.location.reload();
      }
    }

    getFreeProducts() {
      let allFreeProducts = [];
      
      if (this.bundleData.freeItemsData && this.bundleData.freeItemsData.length > 0) {
        this.bundleData.freeItemsData.forEach(containerData => {
          const selectedCount = this.bundleData.products.size;
          
          // Check if this container uses "one for each product" logic
          if (containerData.giftsSettings === 'display_only' && 
              containerData.oneForEachProduct && 
              containerData.oneForEachProductType) {
            
            const productTypeCount = this.getProductCountByType(containerData.oneForEachProductType);
            
            if (productTypeCount > 0 && containerData.freeProducts.length > 0) {
              // Add gifts equal to the number of products of the specified type
              for (let i = 0; i < productTypeCount; i++) {
                allFreeProducts = allFreeProducts.concat(containerData.freeProducts);
              }
            }
          } else {
            // Original logic: Only include free products if minimum requirement is met
            if (selectedCount >= containerData.minItemsRequired) {
              allFreeProducts = allFreeProducts.concat(containerData.freeProducts);
            }
          }
        });
      }
      
      return allFreeProducts;
    }

    parseFreeProductsData(container) {
      try {
        const freeProductsData = JSON.parse(container.dataset.freeProducts || '[]');
        return freeProductsData;
      } catch (error) {
        console.error('Error parsing free products data:', error);
        return [];
      }
    }

    async getCurrentCart() {
      try {
        // Add cache busting to ensure fresh cart data
        const cacheBuster = Date.now();
        const response = await fetch(`/cart.js?_=${cacheBuster}`);
        if (response.ok) {
          const cart = await response.json();
          // console.log('Fresh cart data:', {
          //   item_count: cart.item_count,
          //   items: cart.items.map(item => ({
          //     variant_id: item.variant_id,
          //     quantity: item.quantity,
          //     title: item.title
          //   }))
          // });
          return cart;
        }
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
      return { items: [] };
    }

    async checkVariantAvailability() {
      const modal = this.modal?.querySelector('.product-popup__content');
      if (!modal) {
        console.warn('Modal content not found during availability check');
        return;
      }
      
      const selectButton = modal.querySelector('.product-select-button');
      const availabilityStatus = modal.querySelector('#availability-status');
      const availabilityLoading = availabilityStatus?.querySelector('.availability-loading');

      if (!selectButton || !availabilityStatus) {
        console.warn('Required elements not found in modal for availability check');
        return;
      }

      selectButton.disabled = true;
      selectButton.textContent = 'Checking availability...';
      
      if (availabilityLoading) {
        availabilityLoading.style.display = 'block';
      }
      availabilityStatus.style.display = 'none';

      try {
        // Use the enhanced cart availability check that considers bundle quantities
        const availability = await this.checkCartAvailability(this.selectedVariant.id, 1);

        if (!availability.available) {
          selectButton.textContent = 'Product not available';
          selectButton.disabled = true;
          
          let statusContent = '';
          if (availability.reason.includes('Out of stock')) {
            statusContent = `
              <div class="availability-unavailable">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <span>Out of Stock</span>
              </div>
            `;
          } else {
            statusContent = `
              <div class="availability-unavailable">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 9v3m0 3h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>${availability.reason}</span>
              </div>
            `;
          }
          
          availabilityStatus.innerHTML = statusContent;
          availabilityStatus.style.display = 'flex';
          if (availabilityLoading) {
            availabilityLoading.style.display = 'none';
          }
          return;
        }

        // Show inventory info if tracking is enabled
        let availabilityText = 'Available';
        let statusClass = 'availability-available';
        
        if (availability.inventoryManagement && availability.inventoryQuantity) {
          const totalCommitted = availability.currentCartQuantity + availability.currentBundleQuantity;
          const remaining = availability.inventoryQuantity - totalCommitted;
          
          if (remaining <= 3 && remaining > 0) {
            availabilityText = `Only ${remaining} left`;
            statusClass = 'availability-limited';
          }
          
          // Add bundle context if items already selected
          if (availability.currentBundleQuantity > 0) {
            availabilityText += ` (${availability.currentBundleQuantity} already in bundle)`;
          }
        }

        selectButton.textContent = 'Select This Product';
        selectButton.disabled = false;
        
        const iconSvg = statusClass === 'availability-limited' ? 
          '<path d="M12 9v3m0 3h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' :
          '<path d="M9 11l3 3L22 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
          
        availabilityStatus.innerHTML = `
          <div class="${statusClass}">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              ${iconSvg}
            </svg>
            <span>${availabilityText}</span>
          </div>
        `;
        availabilityStatus.style.display = 'flex';
        if (availabilityLoading) {
          availabilityLoading.style.display = 'none';
        }

      } catch (error) {
        console.error('Error checking variant availability:', error);
        selectButton.textContent = 'Cannot Add';
        selectButton.disabled = true;
        availabilityStatus.innerHTML = `
          <div class="availability-unavailable">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span>Error checking availability</span>
          </div>
        `;
        availabilityStatus.style.display = 'flex';
        if (availabilityLoading) {
          availabilityLoading.style.display = 'none';
        }
      }
    }

    async updateVariantAvailability() {
      // Ensure we have a modal and selected variant before checking availability
      if (!this.modal || !this.selectedVariant || !this.selectedVariant.id) {
        console.warn('Cannot check availability: modal or selected variant not ready');
        return;
      }
      
      // Check if modal is still open and visible
      if (!this.modal.open || this.modal.style.display === 'none') {
        console.warn('Cannot check availability: modal is not open');
        return;
      }
      
      await this.checkVariantAvailability();
    }

    async checkCartAvailability(variantId, requestedQuantity = 1) {
      try {
        // Get current cart to check existing quantities - force fresh fetch
        const cart = await this.getCurrentCart();
        
        // Ensure variantId is a number for comparison
        const numericVariantId = parseInt(variantId);
        
        // Debug logging
        // console.log('Checking availability for variant:', numericVariantId);
        // console.log('Current cart items:', cart.items.map(item => ({
        //   variant_id: item.variant_id,
        //   variant_id_type: typeof item.variant_id,
        //   quantity: item.quantity,
        //   title: item.title
        // })));
        
        // Find existing item in cart - ensure proper type comparison
        const existingItem = cart.items.find(item => {
          const cartVariantId = parseInt(item.variant_id);
          return cartVariantId === numericVariantId;
        });
        
        const currentCartQuantity = existingItem ? existingItem.quantity : 0;
        
        // Get current bundle quantities for this variant
        const currentBundleQuantity = this.bundleData.variantQuantities.get(numericVariantId) || 0;
        
        // console.log('Availability check details:', {
        //   variantId: numericVariantId,
        //   requestedQuantity,
        //   currentCartQuantity,
        //   currentBundleQuantity,
        //   existingItem: existingItem ? { id: existingItem.variant_id, qty: existingItem.quantity, title: existingItem.title } : null
        // });
        
        // Get variant details
        const variantResponse = await fetch(`/variants/${numericVariantId}.js`);
        if (!variantResponse.ok) {
          console.error('Variant not found:', numericVariantId);
          return { available: false, reason: 'Variant not found' };
        }
        
        const variant = await variantResponse.json();
        
        // console.log('Variant details:', {
        //   id: variant.id,
        //   available: variant.available,
        //   inventory_quantity: variant.inventory_quantity,
        //   inventory_management: variant.inventory_management,
        //   inventory_policy: variant.inventory_policy
        // });
        
        // Check if variant is available
        if (!variant.available) {
          return { available: false, reason: 'Out of stock' };
        }
        
        // Check inventory limits if inventory is tracked
        if (variant.inventory_management && variant.inventory_policy === 'deny') {
          const totalRequested = currentCartQuantity + currentBundleQuantity + requestedQuantity;
          
          // console.log('Inventory check:', {
          //   inventory_quantity: variant.inventory_quantity,
          //   currentCartQuantity,
          //   currentBundleQuantity,
          //   requestedQuantity,
          //   totalRequested,
          //   wouldExceed: totalRequested > variant.inventory_quantity
          // });
          
          if (totalRequested > variant.inventory_quantity) {
            if (currentCartQuantity >= variant.inventory_quantity) {
              return { 
                available: false, 
                reason: 'Maximum quantity already in cart',
                currentCartQuantity,
                currentBundleQuantity,
                maxQuantity: variant.inventory_quantity
              };
            } else if (currentCartQuantity + currentBundleQuantity >= variant.inventory_quantity) {
              return {
                available: false,
                reason: 'Already selected maximum available quantity in this bundle',
                currentCartQuantity,
                currentBundleQuantity,
                maxQuantity: variant.inventory_quantity
              };
            } else {
              const available = variant.inventory_quantity - currentCartQuantity - currentBundleQuantity;
              return { 
                available: false, 
                reason: `Only ${available} more can be added (${currentCartQuantity} in cart, ${currentBundleQuantity} in current bundle)`,
                currentCartQuantity,
                currentBundleQuantity,
                maxQuantity: variant.inventory_quantity
              };
            }
          }
        }
        
        return { 
          available: true, 
          currentCartQuantity,
          currentBundleQuantity,
          inventoryQuantity: variant.inventory_quantity,
          inventoryManagement: variant.inventory_management 
        };
        
      } catch (error) {
        console.error('Error checking variant availability:', error);
        return { available: false, reason: 'Error checking availability' };
      }
    }

    resetBundle() {
      this.bundleData.products.forEach((product, selector) => {
        this.removeProductFromBundle(selector);
      });
      
      this.bundleData.products.clear();
      this.bundleData.variantQuantities.clear(); // Clear variant quantities tracking
      this.updateBuyButtonState();
      this.updateChestState();
    }

    getImageUrl(imageSrc, size = '500x500') {
      if (!imageSrc) {
        console.warn('No image source provided');
        return '';
      }
      
      // console.log('Original image src:', imageSrc); // Debug log
      
      // Handle protocol-relative URLs
      let fullImageSrc = imageSrc;
      if (imageSrc.startsWith('//')) {
        fullImageSrc = `https:${imageSrc}`;
      }
      
      // Handle Shopify CDN URLs
      if (fullImageSrc.includes('shopify.com') || fullImageSrc.includes('cdn.shopify.com')) {
        // Remove existing size parameter and add new one
        const cleanSrc = fullImageSrc.replace(/_\d+x\d*(@\d+x)?/, '').replace(/\?.*$/, '');
        const newSrc = cleanSrc.replace(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)$/i, `_${size}.$1`);
        // console.log('Processed image src:', newSrc); // Debug log
        return newSrc;
      }
      
      // For non-Shopify URLs, return as-is
      return fullImageSrc;
    }

    formatPrice(price) {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(price / 100);
    }

    createModal() {
      this.modal = document.createElement('dialog');
      this.modal.className = 'product-popup';
      this.modal.innerHTML = `
        <div class="product-popup__wrapper">
          <button class="product-popup__close" aria-label="Close" type="button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          
          <div class="product-popup__content">
            <div class="product-popup__loader">Loading...</div>
          </div>
        </div>
      `;

      this.modal.querySelector('.product-popup__close').addEventListener('click', () => {
        this.modal.close();
      });

      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) {
          this.modal.close();
        }
      });

      this.modal.addEventListener('keydown', (event) => {
        if (event.code === 'Escape') {
          this.modal.close();
        }
      });

      document.body.appendChild(this.modal);
    }

    updateBundleSummary() {
      const bundleSummaryElement = this.querySelector('.bundle-summary');
      if (!bundleSummaryElement) return;

      const products = Array.from(this.bundleData.products.values());
      
      if (products.length === 0) {
        bundleSummaryElement.innerHTML = '';
        bundleSummaryElement.style.display = 'none';
        return;
      }

      bundleSummaryElement.style.display = 'block';
      const totalPrice = products.reduce((sum, product) => sum + product.price, 0);
      
      // Get free products from all containers
      const allQualifyingFreeProducts = this.getFreeProducts();
      
      const totalItems = products.length + allQualifyingFreeProducts.length;
      
      const summaryHTML = `
        <div class="bundle-summary__content">
          <h4 class="bundle-summary__title">Bundle Summary (${totalItems} item${totalItems !== 1 ? 's' : ''} total)</h4>
          <ul class="bundle-summary__list">
            ${products.map(product => `
              <li class="bundle-summary__item">
                  <span class="bundle-summary__product">${product.title}</span>
                  ${product.variantTitle !== 'Default Title' ? `<span class="bundle-summary__variant"> - ${product.variantTitle}</span>` : ''}
                  ${product.personalization ? `<span class="bundle-summary__personalization"> "${product.personalization}"</span>` : ''}
                  <span class="bundle-summary__price">${this.formatPrice(product.price)}</span>
              </li>
            `).join('')}
            ${allQualifyingFreeProducts.length > 0 ? `
              <li class="bundle-summary__free-header">
                <strong>+ Included Items:</strong>
              </li>
              ${allQualifyingFreeProducts.map(product => `
                <li class="bundle-summary__item bundle-summary__item--free">
                  <span class="bundle-summary__product">${product.title}</span>
                  <span class="bundle-summary__price bundle-summary__price--free">INCLUDED</span>
                </li>
              `).join('')}
            ` : ''}
          </ul>
          <div class="bundle-summary__total">
            <strong>Total: ${this.formatPrice(totalPrice)}</strong>
            ${allQualifyingFreeProducts.length > 0 ? `<span class="bundle-summary__free-value"> + ${allQualifyingFreeProducts.length} included item${allQualifyingFreeProducts.length !== 1 ? 's' : ''}</span>` : ''}
          </div>
        </div>
      `;

      bundleSummaryElement.innerHTML = summaryHTML;
    }

    // Add this new method for bundle validation
    async validateBundlesInCart() {
      if (this.isValidating) return;
      
      try {
        this.isValidating = true;
        console.log('🔍 Validating bundles after cart change...');
        
        const cart = await this.getCurrentCart();
        const bundleGroups = this.groupCartItemsByBundle(cart.items);
        
        // Log all cart items for debugging
        console.log('📋 Current cart items:', cart.items.map((item, index) => ({
          line: index + 1,
          variant_id: item.variant_id,
          title: item.product_title,
          properties: item.properties,
          isBundleItem: item.properties?._bundle_item === 'true',
          isFreeItem: item.properties?._bundle_free_item === 'true',
          bundleId: item.properties?._bundle_id
        })));

        const variantsToRemove = [];

        for (const [bundleId, items] of bundleGroups) {
          const minRequired = this.parseBundleMinRequired(bundleId);
          const paidItems = items.filter(item => 
            item.properties && 
            item.properties._bundle_item === 'true' && 
            item.properties._bundle_free_item !== 'true'
          );
          const freeItems = items.filter(item => 
            item.properties && 
            item.properties._bundle_free_item === 'true'
          );

          console.log(`🔍 Validating bundle ${bundleId}:`, {
            minRequired,
            paidItemCount: paidItems.length,
            freeItemCount: freeItems.length,
            qualifiesForFreeGifts: paidItems.length >= minRequired,
            paidItems: paidItems.map(item => ({ title: item.product_title, variant_id: item.variant_id })),
            freeItems: freeItems.map(item => ({ title: item.product_title, variant_id: item.variant_id }))
          });

          // If bundle no longer meets minimum requirements, remove ALL bundle items
          if (paidItems.length < minRequired && paidItems.length > 0) {
            console.log(`❌ Bundle ${bundleId} no longer meets minimum requirements (${paidItems.length}/${minRequired}). Removing all bundle items.`);
            
            // Mark all remaining paid items for removal
            paidItems.forEach(paidItem => {
              variantsToRemove.push({
                variant_id: paidItem.variant_id,
                bundleId: bundleId,
                title: paidItem.product_title,
                isPaidItem: true
              });
            });
            
            // Mark all free items for removal
            freeItems.forEach(freeItem => {
              variantsToRemove.push({
                variant_id: freeItem.variant_id,
                bundleId: bundleId,
                title: freeItem.product_title,
                isFreeItem: true
              });
            });
          }
          // If bundle qualifies but has excess free gifts, only remove free items
          else if (paidItems.length >= minRequired && freeItems.length > 0) {
            // This handles cases where free gifts were added incorrectly
            // You might want to add logic here to validate the correct number of free gifts
          }
        }

        // Remove invalid bundle items using the theme's cart system
        if (variantsToRemove.length > 0) {
          console.log(`🗑️ Preparing to remove ${variantsToRemove.length} bundle items using theme cart system:`, variantsToRemove);
          
          await this.removeItemsViaThemeSystem(variantsToRemove);
          
          // Force cart refresh after removals
          console.log('🔄 Triggering cart refresh after bundle validation...');
          await this.forceCartRefresh();
          
          // Small delay before allowing next validation
          setTimeout(() => {
            this.isValidating = false;
          }, 3000);
        } else {
          console.log('✅ All bundles are valid - no items to remove');
          this.isValidating = false;
        }

      } catch (error) {
        console.error('❌ Error validating bundles:', error);
        this.isValidating = false;
      }
    }

    // Add this fallback method for individual removals
    async removeItemsIndividually(variantsToRemove) {
      // console.log('🔄 Starting individual item removal process...');
      
      for (const itemToRemove of variantsToRemove) {
        try {
          // Get fresh cart for each removal
          const cart = await this.getCurrentCart();
          
          const lineNumber = cart.items.findIndex(cartItem => 
            cartItem.variant_id === itemToRemove.variant_id &&
            cartItem.properties && 
            cartItem.properties._bundle_id === itemToRemove.bundleId &&
            cartItem.properties._bundle_free_item === 'true'
          ) + 1;

          if (lineNumber > 0) {
            // console.log(`🗑️ Individual removal: "${itemToRemove.title}" at line ${lineNumber}`);
            
            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                line: lineNumber,
                quantity: 0
              })
            });

            if (response.ok) {
              const result = await response.json();
              // console.log(`✅ Individual removal successful: "${itemToRemove.title}". New cart count: ${result.item_count}`);
              // Small delay between individual removals to prevent race conditions
              await new Promise(resolve => setTimeout(resolve, 500));
            } else {
              const errorText = await response.text();
              // console.error(`❌ Individual removal failed for "${itemToRemove.title}":`, {
              //   status: response.status,
              //   error: errorText,
              //   sentPayload: { line: lineNumber, quantity: 0 }
              // });
            }
          } else {
            console.warn(`⚠️ Item "${itemToRemove.title}" not found for individual removal`);
          }
        } catch (error) {
          console.error(`❌ Error during individual removal of "${itemToRemove.title}":`, error);
        }
      }
      
      // console.log('🔄 Individual removal process completed');
    }

    // Add this new method that uses the theme's cart system
    async removeItemsViaThemeSystem(variantsToRemove) {
      console.log('🎯 Using theme cart system for removal...');
      
      for (const itemToRemove of variantsToRemove) {
        try {
          console.log(`🔍 Processing removal for "${itemToRemove.title}" (Variant ID: ${itemToRemove.variant_id})`);
          
          // Find the cart item element in the DOM
          const cartItemElement = document.querySelector(`[data-variant-id="${itemToRemove.variant_id}"]`);
          
          if (cartItemElement) {
            // Try to find a remove button for this item
            const removeButton = cartItemElement.querySelector('[data-cart-remove]') || 
                               cartItemElement.querySelector('[data-id="' + itemToRemove.variant_id + '"]');
            
            if (removeButton) {
              console.log(`🗑️ Found remove button for "${itemToRemove.title}", triggering click...`);
              removeButton.click();
              
              // Wait a bit between removals
              await new Promise(resolve => setTimeout(resolve, 1000));
            } else {
              console.log(`⚠️ No remove button found for "${itemToRemove.title}", trying direct cart update...`);
              await this.directCartRemoval(itemToRemove);
            }
          } else {
            console.log(`⚠️ No cart element found for "${itemToRemove.title}", trying direct cart update...`);
            await this.directCartRemoval(itemToRemove);
          }
        } catch (error) {
          console.error(`❌ Error removing "${itemToRemove.title}" via theme system:`, error);
        }
      }
      
      console.log('🔄 All removals attempted, cart should refresh automatically...');
    }

    // Fallback direct removal method
    async directCartRemoval(itemToRemove) {
      try {
        // Get fresh cart data
        const cart = await this.getCurrentCart();
        
        // Find the item's current line number - FIXED to handle both paid and free items
        const lineNumber = cart.items.findIndex(cartItem => 
          cartItem.variant_id === itemToRemove.variant_id &&
          cartItem.properties && 
          cartItem.properties._bundle_id === itemToRemove.bundleId &&
          (
            // Match either paid bundle items or free bundle items
            cartItem.properties._bundle_item === 'true' ||
            cartItem.properties._bundle_free_item === 'true'
          )
        ) + 1;

        if (lineNumber > 0) {
          console.log(`🗑️ Direct removal: "${itemToRemove.title}" at line ${lineNumber}`);
          
          // Use the same endpoint the theme uses
          const response = await fetch(window.theme?.routes?.cart_change_url || '/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              line: lineNumber,
              quantity: 0
            })
          });

          if (response.ok) {
            console.log(`✅ Direct removal successful: "${itemToRemove.title}"`);
            
            // Don't refresh here - let the main validation method handle it
            // to avoid multiple refreshes
            
          } else {
            const errorText = await response.text();
            console.error(`❌ Direct removal failed for "${itemToRemove.title}":`, errorText);
          }
        } else {
          console.warn(`⚠️ Item "${itemToRemove.title}" not found in fresh cart for removal`);
        }
      } catch (error) {
        console.error(`❌ Error in direct removal of "${itemToRemove.title}":`, error);
      }
    }

    parseBundleMinRequired(bundleId) {
      // Parse bundle ID format: bundle_min2_timestamp_randomString
      const match = bundleId.match(/bundle_min(\d+)_/);
      return match ? parseInt(match[1]) : 2;
    }

    groupCartItemsByBundle(cartItems) {
      const bundles = new Map();
      
      cartItems.forEach((item, index) => {
        if (item.properties && item.properties._bundle_id) {
          const bundleId = item.properties._bundle_id;
          if (!bundles.has(bundleId)) {
            bundles.set(bundleId, []);
          }
          bundles.get(bundleId).push({ ...item, cartIndex: index });
        }
      });

      return bundles;
    }

    async removeInvalidFreeGifts(itemsToRemove) {
      const updates = {};
      itemsToRemove.forEach(item => {
        updates[item.line] = 0; // Set quantity to 0 to remove
      });

      // console.log('🗑️ Removing invalid free gifts:', updates);

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error('Failed to remove invalid free gifts');
      }

      return await response.json();
    }

    refreshCartDisplay() {
      // Trigger cart refresh through the existing cart system
      const cartItems = document.querySelector('cart-items');
      if (cartItems) {
        cartItems.getCart();
      } else {
        // Fallback: dispatch a cart change event to trigger updates
        document.dispatchEvent(
          new CustomEvent('theme:cart:change', {
            bubbles: true,
            detail: {
              cartCount: 0 // Will be updated by the actual cart fetch
            },
          })
        );
      }
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    // Add this method to force cart refresh
    async forceCartRefresh() {
      try {
        // console.log('🔄 Starting forced cart refresh...');
        
        // Method 1: Update cart using the existing cart-items element
        const cartItems = document.querySelector('cart-items');
        if (cartItems && typeof cartItems.getCart === 'function') {
          // console.log('🔄 Refreshing via cart-items element...');
          await cartItems.getCart();
        }
        
        // Method 2: Fetch fresh cart data and dispatch events
        const freshCartResponse = await fetch('/cart.js');
        if (freshCartResponse.ok) {
          const freshCartData = await freshCartResponse.json();
          
          // console.log('🔄 Dispatching cart change event...', {
          //   item_count: freshCartData.item_count
          // });
          
          // Dispatch the cart change event that the theme listens for
          document.dispatchEvent(
            new CustomEvent('theme:cart:change', {
              bubbles: true,
              detail: {
                cartCount: freshCartData.item_count
              },
            })
          );
          
          // Also dispatch a product added event to trigger other cart updates
          document.dispatchEvent(new CustomEvent('theme:product:added', { bubbles: true }));
        }
        
        // Method 3: Update cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.build === 'function') {
          // console.log('🔄 Refreshing cart drawer...');
          // Some themes have a refresh method
          if (typeof cartDrawer.getCart === 'function') {
            await cartDrawer.getCart();
          }
        }
        
        // Method 4: Force refresh of any cart count elements
        this.updateCartCountElements();
        
        // console.log('✅ Cart refresh completed');
        
      } catch (error) {
        console.error('❌ Error during cart refresh:', error);
      }
    }

    // Add method to update cart count elements
    updateCartCountElements() {
      // Update any cart count badges in the header
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count');
      
      // Fetch fresh cart count
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          cartCountElements.forEach(element => {
            element.textContent = cart.item_count;
            element.setAttribute('data-cart-count', cart.item_count);
          });
          
          // console.log(`🔄 Updated ${cartCountElements.length} cart count elements to ${cart.item_count}`);
        })
        .catch(error => {
          console.error('Error updating cart count elements:', error);
        });
    }

    // Add new method to determine product type from selector
    getProductTypeFromSelector(selector) {
      // Look for the parent item to determine type
      const parentItem = selector.closest('.products-content__item');
      if (!parentItem) return null;
      
      const titleElement = parentItem.querySelector('.products-content__item-title');
      if (!titleElement) return null;
      
      const titleText = titleElement.textContent.trim().toLowerCase();
      if (titleText.includes('small folk')) {
        return 'small-folk';
      } else if (titleText.includes('big folk')) {
        return 'big-folk';
      }
      
      return null;
    }

    // Add method to count products by type
    getProductCountByType(productType) {
      let count = 0;
      for (const [selector, productData] of this.bundleData.products) {
        if (productData.productType === productType) {
          count++;
        }
      }
      return count;
    }

    // Add new method to enable next selector
    enableNextSelector(productType) {
      const typeSelectors = this.querySelectorAll(
        `.products-content__item-wrapper-selector[data-product-type="${productType}"]`
      );
      
      // Find the next disabled selector
      const nextDisabled = Array.from(typeSelectors).find(selector => 
        selector.classList.contains('selector-disabled') && 
        selector.style.display === 'none'
      );
      
      if (nextDisabled) {
        // Show and enable the selector with animation
        nextDisabled.style.display = '';
        nextDisabled.classList.remove('selector-disabled');
        nextDisabled.classList.add('selector-enabling');
        
        // Add a brief highlight to show it's newly available
        setTimeout(() => {
          nextDisabled.classList.remove('selector-enabling');
          nextDisabled.classList.add('selector-newly-enabled');
          
          // Remove the highlight after animation
          setTimeout(() => {
            nextDisabled.classList.remove('selector-newly-enabled');
          }, 1000);
        }, 500);
      }
    }

    // Add new method to disable last selector when removing products
    disableLastSelector(productType) {
      const typeSelectors = this.querySelectorAll(
        `.products-content__item-wrapper-selector[data-product-type="${productType}"]`
      );
      
      // Count how many of this type are currently selected
      const selectedCount = Array.from(typeSelectors).filter(selector => 
        selector.classList.contains('selected')
      ).length;
      
      // Find the highest indexed enabled but unselected selector
      const enabledUnselected = Array.from(typeSelectors)
        .filter(selector => 
          !selector.classList.contains('selector-disabled') && 
          !selector.classList.contains('selected') &&
          selector.style.display !== 'none'
        )
        .sort((a, b) => parseInt(b.dataset.selectorIndex) - parseInt(a.dataset.selectorIndex));
      
      // If we have more enabled selectors than needed (keeping 2 minimum + selected count)
      const minToKeep = Math.max(2, selectedCount + 1);
      const currentEnabled = Array.from(typeSelectors).filter(selector => 
        !selector.classList.contains('selector-disabled') && 
        selector.style.display !== 'none'
      ).length;
      
      if (currentEnabled > minToKeep && enabledUnselected.length > 0) {
        const toDisable = enabledUnselected[0];
        toDisable.classList.add('selector-disabled');
        toDisable.style.display = 'none';
        console.log(`🔒 Disabled ${productType} selector (index: ${toDisable.dataset.selectorIndex})`);
      }
    }
  }

  customElements.define('gallery-with-products', GalleryWithProducts);
}